import { useState } from "react";
import { KeyRound, Mail, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { NativeSelect } from "@/components/common/Page";
import { Spinner } from "@/components/common/States";
import { useFetch } from "@/hooks/useFetch";
import { api, errMsg } from "@/lib/api";
import { fmtDate } from "@/lib/format";

const REL = { aluno: "Aluno", responsavel: "Responsável" };

export const PortalAccessCard = ({ studentId, defaultEmail }) => {
  const { data, reload } = useFetch(`/students/${studentId}/portal`);
  const [email, setEmail] = useState(defaultEmail || "");
  const [relation, setRelation] = useState("aluno");
  const [busy, setBusy] = useState(false);

  const invite = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data: res } = await api.post(`/students/${studentId}/portal/invite`, { email, relation });
      toast.success(res.message);
      setEmail("");
      reload();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const revoke = async (id) => {
    try {
      await api.delete(`/students/${studentId}/portal/${id}`);
      toast.success("Acesso removido");
      reload();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const rows = [...(data?.accounts || []).map((a) => ({ ...a, kind: "account" })), ...(data?.invites || []).map((i) => ({ ...i, kind: "invite" }))];

  return (
    <div className="rounded-xl border bg-card p-5" data-testid="portal-access-card">
      <div className="mb-1 flex items-center gap-2"><KeyRound className="h-4 w-4 text-primary" /><h3 className="font-heading font-semibold">Portal do aluno</h3></div>
      <p className="mb-4 text-sm text-muted-foreground">Convide o aluno ou o responsável para acompanhar aulas, presença, notas e pagamentos. Ele só verá os dados deste aluno.</p>
      <form onSubmit={invite} className="flex flex-col gap-2 sm:flex-row">
        <Input type="email" placeholder="E-mail do aluno ou responsável" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="portal-invite-email-input" />
        <NativeSelect className="sm:w-40" value={relation} onChange={(e) => setRelation(e.target.value)} data-testid="portal-invite-relation-select">
          <option value="aluno">Aluno</option><option value="responsavel">Responsável</option>
        </NativeSelect>
        <Button type="submit" disabled={busy} data-testid="portal-invite-submit-btn">{busy ? <Spinner className="mr-2" /> : <Mail className="mr-2 h-4 w-4" />}Enviar convite</Button>
      </form>
      {rows.length > 0 && (
        <div className="mt-4 divide-y rounded-lg border">
          {rows.map((r) => (
            <div key={r.id} className="flex items-center gap-3 px-3 py-2 text-sm" data-testid={`portal-access-${r.id}`}>
              <div className="min-w-0 flex-1">
                <p className="truncate font-medium">{r.name || r.email}</p>
                <p className="text-xs text-muted-foreground">{REL[r.relation]} · {r.kind === "account" ? `Acesso ativo${r.last_login_at ? ` · último acesso ${fmtDate(r.last_login_at.slice(0, 10))}` : ""}` : `Convite pendente até ${fmtDate(r.expires_at.slice(0, 10))}`}</p>
              </div>
              <button onClick={() => revoke(r.id)} className="rounded-md p-2 text-muted-foreground hover:bg-muted hover:text-destructive" aria-label="Remover acesso" data-testid={`portal-revoke-${r.id}`}><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
